import { useState } from "react";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { CUSTOMERS } from "../data/content";

const FILTERS = ["All", ...Array.from(new Set(CUSTOMERS.map((c) => c.engagement)))];

export default function Customers() {
  const [filter, setFilter] = useState("All");
  const visible = filter === "All" ? CUSTOMERS : CUSTOMERS.filter((c) => c.engagement === filter);

  return (
    <>
      <SEO
        title="Customers & Engagements | ZasyaOnline"
        description="Approved customer references across customised software, MoveOS deployments, infrastructure services and contact-centre consulting and implementation."
        path="/customers"
      />
      <section className="pt-28 pb-14 sm:pt-36 sm:pb-16" data-testid="customers-hero">
        <div className="container-x max-w-3xl">
          <Reveal>
            <span className="eyebrow">Customers &amp; Engagements</span>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
              Work we're permitted to talk about.
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-600">
              A selection of approved customer references and engagement types. We
              don't publish fabricated testimonials or invented metrics — just what
              we did, and for whom.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20" data-testid="customers-list">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filter engagements">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors duration-150 ${
                    filter === f
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-slate-300 bg-white text-slate-600 hover:border-blue-400 hover:text-blue-700"
                  }`}
                  data-testid={`customers-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((c, i) => (
              <Reveal key={c.name} delay={Math.min(i * 0.05, 0.25)} className="h-full">
                <article className="card-line h-full p-6" data-testid={`customer-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-teal-700">
                    {c.engagement}
                  </span>
                  <h2 className="mt-2 font-display text-xl font-bold tracking-tight text-slate-900">
                    {c.name}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.description}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-xs leading-relaxed text-slate-500">
              Additional references are available during an enquiry conversation,
              subject to each customer's approval.
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection
        eyebrow="Become a reference"
        title="Your operation could be next on this page."
        body="Talk to us about a product deployment or consulting engagement — and judge us on the work."
      />
    </>
  );
}
